import { Box, Typography } from "@mui/material";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { ShowChart } from "@mui/icons-material";
import { MONITOR_COLORS } from "../../config/functions";

function getBarColor(total) {
    if (total >= 6) return MONITOR_COLORS.status.error.secondary;
    if (total >= 3) return MONITOR_COLORS.status.warning.secondary;
    return MONITOR_COLORS.equipment.machine;
}

const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;

    return (
        <Box sx={{
            backgroundColor: MONITOR_COLORS.secondary,
            border: '1px solid ' + MONITOR_COLORS.tertiary,
            borderRadius: 1.5,
            px: 1.5,
            py: 1,
        }}>
            <Typography sx={{ fontSize: '0.75rem', color: MONITOR_COLORS.text.secondary }}>
                {label}
            </Typography>

            <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: MONITOR_COLORS.text.primary }}>
                {payload[0].value} erros
            </Typography>
        </Box>
    );
};

export function MonitorLast12HoursError({ requestErrors }) {
    const porHora = requestErrors?.por_hora ?? [];

    const data = [...porHora].sort((a, b) => {
        const toMinutes = (hora) => {
            const [h, m = 0] = hora.split(':').map(Number);
            return h * 60 + m;
        };

        const now = new Date();
        const currentMinutes = now.getHours() * 60 + now.getMinutes();

        const normalize = (hora) => {
            const minutes = toMinutes(hora);
            return minutes > currentMinutes ? minutes - 1440 : minutes;
        };

        return normalize(a.hora) - normalize(b.hora);
    });

    return (
        <Box sx={{
            backgroundColor: MONITOR_COLORS.secondary,
            border: '1px solid ' + MONITOR_COLORS.tertiary,
            borderRadius: 3,
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            height: '100%',
            marginTop: '2vh',
            marginRight: '5vw'
        }}>
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 2,
                flexShrink: 0,
                backgroundColor: MONITOR_COLORS.primary,
                borderRadius: '12px 12px 0 0',
                borderBottom: '1px solid ' + MONITOR_COLORS.tertiary,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ShowChart sx={{ color: MONITOR_COLORS.text.primary, fontSize: { xs: 18, xl: 22 }, marginRight: '1vw' }} />
                    <Typography sx={{ fontWeight: 700, color: MONITOR_COLORS.text.primary, fontSize: { xs: '0.9rem', xl: '1rem' } }}>
                        Erros por hora (12h)
                    </Typography>
                </Box>
            </Box>

            <Box sx={{
                flex: 1,
                backgroundColor: MONITOR_COLORS.secondary,
                borderRadius: '0 0 12px 12px',
                p: { xs: 1.5, xl: 2 },
            }}>
                {data.length === 0 ? (
                    <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography sx={{ fontSize: '0.8rem', color: MONITOR_COLORS.text.secondary }}>
                            Sem erros nas últimas 12h ✓
                        </Typography>
                    </Box>
                ) : (
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={data} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
                            <XAxis
                                dataKey="hora"
                                tick={{ fill: MONITOR_COLORS.text.secondary, fontSize: 11 }}
                                axisLine={false}
                                tickLine={false}
                            />

                            <YAxis
                                tick={{ fill: MONITOR_COLORS.text.secondary, fontSize: 11 }}
                                axisLine={false}
                                tickLine={false}
                                allowDecimals={false}
                            />

                            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.05)' }} />

                            <Bar dataKey="total" radius={[4, 4, 0, 0]} maxBarSize={40}>
                                {data.map((entry, index) => (
                                    <Cell key={index} fill={getBarColor(entry.total)} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                )}
            </Box>
        </Box>
    );
}